import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CircularProgress, Grid, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Alert,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import axios from 'axios';
import { fmtMoney, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtCur  = (v) => fmtMoney(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function ParentStatements() {
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [stmt, setStmt] = useState(null); // { invoices, receipts, totalBilled, totalPaid, balance }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lRes = await axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() });
        const kids = lRes.data?.data || [];
        setLearners(kids);
        if (kids.length > 0) {
          setSelectedLearner(kids[0]);
          loadStatement(kids[0].admNo);
        }
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadStatement = async (admNo) => {
    try {
      const res = await axios.get(`${API}/api/finance/statements/${admNo}`, { headers: hdrs() });
      setStmt(res.data || null);
    } catch (e) {
      console.error('Error loading statement:', e);
    }
  };

  // Merge invoices (billed) and receipts (paid) into one chronological ledger
  // with a running balance — like a BC customer ledger.
  const entries = stmt ? [
    ...(stmt.invoices || []).map(i => ({
      date: i.date, ref: i.invoiceNo,
      description: i.description + (i.term ? ` (Term ${i.term}${i.academicYear ? ` ${i.academicYear}` : ''})` : ''),
      billed: i.amount || 0, paid: 0,
    })),
    ...(stmt.receipts || []).map(r => ({
      date: r.paymentDate, ref: r.receiptNo,
      description: r.transactionDescription || 'Payment received',
      billed: 0, paid: r.amount || 0,
    })),
  ].sort((a, b) => new Date(a.date) - new Date(b.date)) : [];

  let running = 0;
  const rows = entries.map(e => ({ ...e, balance: (running += e.billed - e.paid) }));

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box py={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>📋 Fee Statements</Typography>

        <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
          Full account statement — every invoice and payment with a running balance. Read-only.
        </Alert>

        {/* Learner Selector */}
        {learners.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
            {learners.map(l => (
              <Chip
                key={l._id}
                label={l.name}
                onClick={() => {
                  setSelectedLearner(l);
                  loadStatement(l.admNo);
                }}
                color={selectedLearner?._id === l._id ? 'primary' : 'default'}
                variant={selectedLearner?._id === l._id ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {/* Totals */}
        {stmt && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'TOTAL BILLED', value: stmt.totalBilled, color: 'text.primary' },
              { label: 'TOTAL PAID',   value: stmt.totalPaid,   color: 'success.main' },
              { label: 'BALANCE',      value: stmt.balance,     color: stmt.balance > 0 ? 'warning.main' : 'success.main' },
            ].map(c => (
              <Grid item xs={4} key={c.label}>
                <Card sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">{c.label}</Typography>
                  <Typography fontWeight={700} color={c.color}
                    sx={{ fontSize: { xs: 14, sm: 20 } }}>
                    {fmtCur(c.value)}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Ledger */}
        {rows.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No transactions yet</Typography>
          </Card>
        ) : (
          <>
            {/* Mobile: Card View */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5 }}>
              {rows.map((r, i) => (
                <Card key={i} sx={{ p: 1.5, borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #eee', bgcolor: r.paid > 0 ? 'success.50' : '#fff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: '#333', wordBreak: 'break-word' }}>{r.description}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {fmtDate(r.date)}{r.ref ? ` • ` : ''}<Box component="span" sx={{ fontFamily: 'monospace' }}>{r.ref}</Box>
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>BILLED</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{r.billed ? fmtCur(r.billed) : '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>PAID</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'success.main' }}>{r.paid ? fmtCur(r.paid) : '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>BALANCE</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{fmtCur(r.balance)}</Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>

            {/* Desktop: Table View */}
            <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Ref</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Billed</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Paid</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={i} hover sx={r.paid > 0 ? { bgcolor: 'success.50' } : undefined}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</TableCell>
                        <TableCell><Typography variant="caption" fontFamily="monospace">{r.ref}</Typography></TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell align="right">{r.billed ? fmtCur(r.billed) : ''}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>{r.paid ? fmtCur(r.paid) : ''}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCur(r.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </>
        )}
      </Box>
    </Container>
  );
}
